import React, { useState, useEffect, useRef } from "react";
import RichText from "components/rich-text";
import { Container } from "react-bootstrap";
import getImagePath from "helpers/get-image-path";
import { lazyLoadVideos } from "helpers/lazyLoadVideos";
import * as styles from "./style.mod.scss";
import { useMediaQuery } from "react-responsive";
import DraggableDivider from "assets/svgs/draggable-divider.svg";
import WhiteArrowDown from "assets/svgs/white-arrow-down.svg";
import locale from "../../locales";
import { useLocation } from "@reach/router";

const ImageComparisonWithText = ({ data }) => {
  const { title, description, steps, images, secondTitle, secondDescription, video } = data;
  const [dragging, setDragging] = useState(false);
  const [viewMore, setViewMore] = useState(false);
  const [dividerPosition, setDividerPosition] = useState(50);
  const isMobile = useMediaQuery({ query: "(max-width: 991px)" });
  const videoRef = useRef(null);
  const location = useLocation();
  const isFoodboom = location.pathname.includes("foodboom");

  const handleMouseDown = () => {
    setDragging(true);
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleMouseMove = (event) => {
    if (dragging) {
      const containerRect = event.target.closest(`.${styles.imagesContainer}`).getBoundingClientRect();
      const newPosition = ((event.clientX - containerRect.left) / containerRect.width) * 100;
      setDividerPosition(Math.max(0, Math.min(100, newPosition)));
    }
  };

  const handleTouchStart = () => {
    setDragging(true);
  };

  const handleTouchEnd = () => {
    setDragging(false);
  };

  const handleTouchMove = (event) => {
    if (dragging) {
      const touch = event.touches[0];
      const containerRect = touch.target.closest(`.${styles.imagesContainer}`).getBoundingClientRect();
      const newPosition = ((touch.clientX - containerRect.left) / containerRect.width) * 100;
      setDividerPosition(Math.max(0, Math.min(100, newPosition)));
    }
  };

  useEffect(() => {
    lazyLoadVideos();
  }, []);

  useEffect(() => {
    if (viewMore && videoRef.current) {
      videoRef.current.onloadeddata = () => {
        videoRef.current.play().catch(error => {
          console.error("Error playing video:", error);
        });
      };
      videoRef.current.load();
    }
  }, [viewMore]);

  return (
    <>
      <Container>
        <div className={styles.container}>
          <RichText blocks={title} className={styles.title} />
          <p className={styles.description}>{description}</p>
          <div className={styles.content}>
            <div
              className={styles.imagesContainer}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img
                src={getImagePath(images[0])}
                alt={images[0].alt}
                className={styles.image}
              />
              <img
                src={getImagePath(images[1])}
                alt={images[1].alt}
                className={styles.image}
                style={{ clipPath: `inset(0 0 0 ${dividerPosition}%)` }}
              />
              <div
                className={styles.divider}
                style={{ 
                  left: `${dividerPosition}%`, backgroundColor: isFoodboom && "#e5007d" 
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
              >
                <DraggableDivider width={20} className={styles.draggableSvg} />
              </div>
            </div>
            <div className={styles.stepsContainer}>
              {steps.map((step, index) => {
                return (
                  <div key={index} className={isFoodboom ? styles.stepPink : styles.step}>
                    <div className={styles.circle}>
                      <p className={styles.stepCount}>{index + 1}</p>
                    </div>
                    <p className={styles.stepTitle}>{step.title}</p>
                    <p className={styles.stepSubtitle}>{step.subtitle}</p>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </Container>
      <div className={styles.hideSecondContainer}>
        <div
          onClick={() => setViewMore(!viewMore)}
          style={{ display: viewMore ? "none" : "flex" }}
          className={isFoodboom ? styles.viewMorePink : styles.viewMore}
        >
          <p>
            {locale.pages.panLanding.viewMore}
          </p>
          <WhiteArrowDown />
        </div>
      </div>
      <div
        style={!viewMore ? { maxHeight: isMobile ? "130px" : "200px" } : { height: "auto" }}
        className={styles.secondSection}
      >
        <Container>
          <>
            <div className={styles.secondContainer}>
              <div className={styles.textContainer}>
                <h1>{secondTitle}</h1>
                <p>{secondDescription}</p>
              </div>
              <div className={styles.videoContainer}>
                <video
                  ref={videoRef}
                  className={`${styles.smallVideo} lazyload`}
                  type="video/mp4"
                  autoPlay={viewMore}
                  muted
                  playsInline
                  loop
                >
                  <source data-src={video.url} />
                  Sorry, your browser doesnt support embedded videos.
                </video>
              </div>
            </div>
            {!viewMore && <div className={styles.backdrop}></div>}
          </>
        </Container>
      </div>
    </>
  );
};

export default ImageComparisonWithText;
